import { PreviewSMSMessage } from "@/schema"
import { unescapeWhitespace } from "@/utils/text-helpers"
import { format } from "date-fns"
import { ArrowDownLeft, ArrowUpRight, Calendar, ExternalLink, Phone } from "lucide-react"

import { usePeekViewData } from "@/hooks/api/peek-views/queries"
import { Link as BookLink } from "@/components/book-link"

import PeekViewSkeleton from "./skeleton"

export default function SMSMessagePeekView({ smsMessageId, href }: { smsMessageId: string; href: string | null }) {
  const { data: sms, isLoading } = usePeekViewData(smsMessageId, "SMSMessage") as {
    data: PreviewSMSMessage
    isLoading: boolean
  }

  if (isLoading) return <PeekViewSkeleton className="max-w-80" />

  const dateText = sms.sent_at
    ? format(
        sms.sent_at,
        new Date(sms.sent_at).getFullYear() === new Date().getFullYear() ? "MMM d · h:mm a" : "MMM d, yyyy · h:mm a"
      )
    : "Unknown"
  const isOutgoing = sms.direction === "outgoing"

  return (
    <div className="text-tertiary-foreground flex w-full max-w-80 flex-col items-stretch gap-2">
      <div className="mb-0.5 flex items-baseline gap-2">
        {isOutgoing ? (
          <ArrowUpRight className="text-blue-accent mt-0.5 size-4 flex-none" />
        ) : (
          <ArrowDownLeft className="text-green-accent mt-0.5 size-4 flex-none" />
        )}
        <p className="line-clamp-2 font-medium">{isOutgoing ? "Outgoing SMS" : "Incoming SMS"}</p>
        {href && (
          <BookLink
            href={href}
            className="text-muted-foreground hover:text-link ml-auto flex-none transition-colors"
            onClick={(e) => e.stopPropagation()}
            aria-label="Open in new page"
          >
            <ExternalLink className="size-3.5" strokeWidth={1.5} />
          </BookLink>
        )}
      </div>
      {sms.message && <p className="text-muted-foreground line-clamp-2 text-sm">{unescapeWhitespace(sms.message)}</p>}
      <div className="flex items-center gap-2">
        <Calendar className="text-icon size-4 flex-none" />
        <p className="text-sm">{dateText}</p>
      </div>
      {sms.phone_number && (
        <div className="flex items-center gap-2">
          <Phone className="text-icon size-4 flex-none" />
          <p className="truncate text-sm">{sms.phone_number}</p>
        </div>
      )}
    </div>
  )
}
